import { useState, useCallback, useMemo } from "react";
import { PortfolioItem } from "@/hooks/useProfile";
import { CaseStudyItem } from "@/types/case-study";
import { 
  UnifiedPortfolioItem, 
  portfolioItemToUnified, 
  caseStudyToUnified,
  unifiedToPortfolioItem,
  unifiedToCaseStudy
} from "@/types/unified-portfolio";
import { useCaseStudy } from "@/hooks/useCaseStudy";
import { toast } from 'sonner';
import { useQueryClient } from "@tanstack/react-query";

interface UseUnifiedPortfolioProps {
  portfolioItems: PortfolioItem[];
  caseStudies: CaseStudyItem[];
  onPortfolioChange: (items: PortfolioItem[]) => void;
  onCaseStudyChange?: (items: CaseStudyItem[]) => void;
  isVerified?: boolean;
}

export const useUnifiedPortfolio = ({
  portfolioItems,
  caseStudies,
  onPortfolioChange,
  onCaseStudyChange,
  isVerified = false,
}: UseUnifiedPortfolioProps) => {
  const { updateCaseStudy } = useCaseStudy();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; type: string; title: string } | null>(null);

  // Convert both types to unified items and combine them
  const unifiedItems = useMemo(() => {
    const portfolioUnified = portfolioItems.map(portfolioItemToUnified);
    const caseStudyUnified = caseStudies.map(caseStudyToUnified);
    
    // Debug logging (can be removed after verification)
    // console.log('useUnifiedPortfolio - Portfolio items:', portfolioItems);
    // console.log('useUnifiedPortfolio - Case studies:', caseStudies);
    // console.log('useUnifiedPortfolio - Featured portfolio items:', portfolioItems.filter(p => p.is_featured));
    // console.log('useUnifiedPortfolio - Featured case studies:', caseStudies.filter(c => c.is_featured));
    
    // Combine items without enforcing any sort order
    // Let the parent component handle sorting based on user's selected sort order
    const combined = [...portfolioUnified, ...caseStudyUnified];
    
    // console.log('useUnifiedPortfolio - Unified items:', combined);
    // console.log('useUnifiedPortfolio - Featured unified items:', combined.filter(item => item.is_featured));
    
    return combined;
  }, [portfolioItems, caseStudies]);

  const handleOperation = useCallback(async (operation: () => Promise<void>, errorMessage: string) => {
    setIsProcessing(true);
    try {
      await operation();
    } catch (error) {
      console.error(errorMessage, error);
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleRemove = useCallback((id: string) => {
    const item = unifiedItems.find(item => item.id === id);
    if (!item) return;

    // Set up the item to delete and show dialog
    setItemToDelete({
      id,
      type: item.type === "shot" ? "portfolio item" : "case study",
      title: item.title
    });
    setDeleteDialogOpen(true);
  }, [unifiedItems]);

  const handleDeleteConfirm = useCallback(() => {
    if (!itemToDelete) return;

    // console.log('Deleting item:', itemToDelete.type, itemToDelete.id);

    if (itemToDelete.type === "portfolio item") {
      // Remove from portfolio items
      const updatedItems = portfolioItems.filter(p => p.id !== itemToDelete.id);
      onPortfolioChange(updatedItems);
      toast.success("Portfolio item deleted successfully");
    } else if (itemToDelete.type === "case study") {
      // Delete case study using the case study service
      import("@/services/supabase/caseStudies").then(({ CaseStudyService }) => {
        CaseStudyService.deleteCaseStudy(itemToDelete.id).then(async () => {
          // Update the case studies list
          if (onCaseStudyChange) {
            const updatedCaseStudies = caseStudies.filter(cs => cs.id !== itemToDelete.id);
            onCaseStudyChange(updatedCaseStudies);
          }
          
          // Invalidate queries to refresh the data
          await queryClient.invalidateQueries({ queryKey: ['case-studies'] });
          await queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
          await queryClient.invalidateQueries({ queryKey: ['unified-case-studies'] });
          
          toast.success("Case study deleted successfully");
        }).catch((error) => {
          console.error('Error deleting case study:', error);
          toast.error("Failed to delete case study");
        });
      });
    }

    // Close dialog and reset state
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  }, [itemToDelete, portfolioItems, caseStudies, onPortfolioChange, onCaseStudyChange]);

  const handleEdit = useCallback((id: string) => {
    const item = unifiedItems.find(item => item.id === id);
    if (!item) return;

    if (item.type === "shot") {
      // Navigate to portfolio item edit
      // This would be handled by the parent component
      // console.log("Edit portfolio item:", id);
    } else if (item.type === "case_study") {
      // Navigate to case study edit
      // This would be handled by the parent component
      // console.log("Edit case study:", id);
    }
  }, [unifiedItems]);

  const handleRename = useCallback((id: string, newTitle: string) => {
    const item = unifiedItems.find(item => item.id === id);
    if (!item) return;

    handleOperation(async () => {
      if (item.type === "shot") {
        const updatedItems = portfolioItems.map(p => 
          p.id === id ? { ...p, title: newTitle } : p
        );
        onPortfolioChange(updatedItems);
        toast.success("Portfolio item renamed successfully");
      } else if (item.type === "case_study") {
        // Update case study title
        await updateCaseStudy.mutateAsync({
          id,
          title: newTitle,
        });
        toast.success("Case study renamed successfully");
      }
    }, "Failed to rename item");
  }, [unifiedItems, portfolioItems, onPortfolioChange, updateCaseStudy, handleOperation]);

  const handleReorder = useCallback((reorderedItems: UnifiedPortfolioItem[]) => {
    handleOperation(async () => {
      // Update order for both portfolio items and case studies
      const portfolioUpdates: PortfolioItem[] = [];
      const caseStudyUpdates: CaseStudyItem[] = [];

      reorderedItems.forEach((item, index) => {
        if (item.type === "shot") {
          const portfolioItem = unifiedToPortfolioItem(item);
          portfolioUpdates.push({ 
            ...portfolioItem, 
            order: index,
            // Keep the existing featured status from the reordered item
            is_featured: item.is_featured,
            // Auto-publish if featured (moved to first position)
            status: item.is_featured ? "published" : item.status
          });
        } else if (item.type === "case_study") {
          const caseStudy = unifiedToCaseStudy(item);
          caseStudyUpdates.push({ 
            ...caseStudy, 
            order: index,
            // Keep the existing featured status from the reordered item
            is_featured: item.is_featured,
            // Auto-publish if featured (moved to first position)
            status: item.is_featured ? "published" : item.status
          });
        }
      });

      // Update portfolio items
      if (portfolioUpdates.length > 0) {
        onPortfolioChange(portfolioUpdates);
      }

      // Update case studies
      if (caseStudyUpdates.length > 0 && onCaseStudyChange) {
        onCaseStudyChange(caseStudyUpdates);
      }

      // Update featured status in database for case studies only
      const featuredCaseStudies = reorderedItems.filter(item => item.type === "case_study" && item.is_featured);
      const nonFeaturedCaseStudies = reorderedItems.filter(item => item.type === "case_study" && !item.is_featured);

      // Set featured case studies
      for (const caseStudy of featuredCaseStudies) {
        await updateCaseStudy.mutateAsync({
          id: caseStudy.id,
          is_featured: true,
          status: "published", // Auto-publish when featured
        });
      }

      // Remove featured status from non-featured case studies
      for (const caseStudy of nonFeaturedCaseStudies) {
        if (caseStudies.find(cs => cs.id === caseStudy.id)?.is_featured) {
          // console.log('Removing featured status from case study during reorder:', caseStudy.id);
          await updateCaseStudy.mutateAsync({
            id: caseStudy.id,
            is_featured: false,
          });
        }
      }

      // Force refresh case studies query
      await queryClient.invalidateQueries({ queryKey: ['case-studies'] });

      toast.success("Portfolio order updated successfully");
    }, "Failed to reorder items");
  }, [portfolioItems, caseStudies, onPortfolioChange, onCaseStudyChange, updateCaseStudy, queryClient, handleOperation]);

  const handleSetFeatured = useCallback((id: string) => {
    const item = unifiedItems.find(item => item.id === id);
    if (!item) return;

    // Optimistic update - update local state first for instant UI response
    // console.log('Setting featured item:', id, item.type);
    
    // Update portfolio items immediately
    const updatedPortfolioItems = portfolioItems.map(p => ({ ...p, is_featured: false }));
    onPortfolioChange(updatedPortfolioItems);

    // Update case studies immediately
    if (onCaseStudyChange) {
      const updatedCaseStudies = caseStudies.map(c => ({ ...c, is_featured: false }));
      onCaseStudyChange(updatedCaseStudies);
    }

    // Set the selected item as featured and move it to the top
    if (item.type === "shot") {
      const updatedItems = updatedPortfolioItems.map(p => 
        p.id === id ? { 
          ...p, 
          is_featured: true, 
          status: "published", // Auto-publish when setting as featured
          order: 0 
        } : { ...p, order: (p.order || 0) + 1 }
      );
      // Sort by order to ensure featured item is first
      const sortedItems = updatedItems.sort((a, b) => (a.order || 0) - (b.order || 0));
      onPortfolioChange(sortedItems);
    } else if (item.type === "case_study") {
      // Update case study immediately
      if (onCaseStudyChange) {
        const updatedCaseStudies = caseStudies.map(c => 
          c.id === id 
            ? { ...c, is_featured: true, status: "published", order: 0 }
            : { ...c, order: (c.order || 0) + 1 }
        );
        const sortedCaseStudies = updatedCaseStudies.sort((a, b) => (a.order || 0) - (b.order || 0));
        onCaseStudyChange(sortedCaseStudies);
      }
    }

    // Show success message immediately
    toast.success("Item published and set as featured");

    // Then sync with database in the background
    if (item.type === "case_study") {
      handleOperation(async () => {
        // Remove featured status from all case studies in database
        for (const caseStudy of caseStudies) {
          if (caseStudy.is_featured) {
            // console.log('Removing featured status from case study:', caseStudy.id);
            await updateCaseStudy.mutateAsync({
              id: caseStudy.id,
              is_featured: false,
            });
          }
        }

        // Set the selected case study as featured in database
        // console.log('Setting case study as featured in database:', id);
        try {
          await updateCaseStudy.mutateAsync({
            id,
            is_featured: true,
            status: "published", // Auto-publish when setting as featured
          });
          // console.log('Successfully updated case study in database');
        } catch (error) {
          console.error('Error updating case study in database:', error);
          throw error;
        }

        // Force refresh case studies query
        await queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      }, "Failed to sync featured status with database");
    }
  }, [unifiedItems, portfolioItems, caseStudies, onPortfolioChange, onCaseStudyChange, updateCaseStudy, queryClient, handleOperation]);

  const handleUpdateStatus = useCallback((id: string, status: "draft" | "published") => {
    console.log('handleUpdateStatus called with id:', id, 'status:', status);
    const item = unifiedItems.find(item => item.id === id);
    console.log('Found item for status update:', item);
    if (!item) {
      console.log('Item not found for status update');
      return;
    }

    // Optimistic update - update local state first for instant UI response
    if (item.type === "shot") {
      console.log('Updating shot status');
      const updatedItems = portfolioItems.map(p => 
        p.id === id
          ? { ...p, status, ...(status === "draft" ? { is_featured: false } : {}) }
          : p
      );
      onPortfolioChange(updatedItems);
      const shotMessage = status === "draft"
        ? "Shot saved as draft successfully"
        : "Shot published successfully";
      toast.success(shotMessage);
    } else if (item.type === "case_study") {
      console.log('Updating case study status, onCaseStudyChange available:', !!onCaseStudyChange);
      // Update case study immediately
      if (onCaseStudyChange) {
        const updatedCaseStudies = caseStudies.map(c => 
          c.id === id
            ? { ...c, status, ...(status === "draft" ? { is_featured: false } : {}) }
            : c
        );
        onCaseStudyChange(updatedCaseStudies);
      }
      const caseStudyMessage = status === "draft"
        ? "Case study saved as draft successfully"
        : "Case study published successfully";
      toast.success(caseStudyMessage);
    }

    // Then sync with database in the background
    if (item.type === "case_study") {
      handleOperation(async () => {
        await updateCaseStudy.mutateAsync({
          id,
          status,
          ...(status === "draft" ? { is_featured: false } : {}),
        });
        // Force refresh case studies query
        await queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      }, `Failed to ${status} case study in database`);
    }
  }, [unifiedItems, portfolioItems, caseStudies, onPortfolioChange, onCaseStudyChange, updateCaseStudy, queryClient, handleOperation]);

  return {
    unifiedItems,
    isProcessing,
    handleRemove,
    handleEdit,
    handleRename,
    handleReorder,
    handleSetFeatured,
    handleUpdateStatus,
    // Dialog state and handlers
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    setItemToDelete,
    handleDeleteConfirm,
  };
};
